import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

function FunnelBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round(value / total * 100) : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1"><span className="text-slate-300">{label}</span><span className="text-white font-bold">{value}</span></div>
      <div className="w-full bg-slate-700 rounded-full h-2"><div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${Math.max(pct, 2)}%` }} /></div>
    </div>
  );
}

function ProspectDetail({ prospect, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/api/sales-agent/prospect-detail/${prospect.id}`, { credentials: 'include' });
      if (res.ok) setDetail(await res.json());
      setLoading(false);
    })();
  }, [prospect.id]);

  if (loading) return <p className="text-slate-400 text-sm p-4">Cargando...</p>;
  if (!detail) return null;

  const p = detail.prospect;
  const analysis = detail.analysis || {};

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-white text-lg font-bold">{p.company}</h2>
          <p className="text-slate-400 text-sm">{p.industry} | {p.city}, {p.state} | Score: <span className="text-green-400 font-bold">{p.compatibility_score}%</span></p>
          {p.contact_name && <p className="text-blue-400 text-sm mt-1">{p.contact_name} {p.contact_title ? `(${p.contact_title})` : ''}</p>}
          {p.email && <p className="text-slate-400 text-xs">{p.email} | {p.phone}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={onClose}>Cerrar</Button>
      </div>

      {analysis.recommended_services && (
        <div className="flex flex-wrap gap-1">
          {analysis.recommended_services.map((s, i) => <span key={i} className="text-xs bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded">{s}</span>)}
        </div>
      )}

      {analysis.approach_strategy && <p className="text-slate-300 text-xs italic bg-slate-900 p-3 rounded">{analysis.approach_strategy}</p>}
      {analysis.estimated_project_value && <p className="text-green-400 text-sm font-semibold">Valor estimado: {analysis.estimated_project_value}</p>}

      {/* Linked data */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        {detail.linked_lead && <div className="bg-slate-900 p-2 rounded"><p className="text-slate-500">Lead vinculado</p><p className="text-white">{detail.linked_lead.name}</p></div>}
        {detail.linked_client && <div className="bg-slate-900 p-2 rounded"><p className="text-slate-500">Cliente</p><p className="text-white">{detail.linked_client.name}</p></div>}
        {detail.quote && <div className="bg-slate-900 p-2 rounded"><p className="text-slate-500">Cotizacion</p><p className="text-white">{detail.quote.quote_number} (${detail.quote.total?.toLocaleString()})</p></div>}
      </div>

      {/* Timeline */}
      <div>
        <h4 className="text-slate-300 text-sm font-semibold mb-2">Timeline ({detail.timeline?.length || 0})</h4>
        <div className="max-h-48 overflow-y-auto space-y-1">
          {(detail.timeline || []).map(t => (
            <div key={t.id} className="bg-slate-900 p-2 rounded text-xs flex gap-2">
              <span className={`px-1.5 py-0.5 rounded ${t.type === 'call' ? 'bg-purple-900/50 text-purple-300' : t.type === 'meeting' ? 'bg-cyan-900/50 text-cyan-300' : t.type === 'email' ? 'bg-blue-900/50 text-blue-300' : 'bg-slate-700 text-slate-300'}`}>{t.type}</span>
              <span className="text-slate-400 flex-1">{t.notes}</span>
              <span className="text-slate-600">{t.created_at?.substring(0, 10)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function B2BUnifiedPage() {
  const [dashboard, setDashboard] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [bids, setBids] = useState([]);
  const [discoveries, setDiscoveries] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, prosRes, bidsRes, discRes] = await Promise.all([
        fetch(`${API}/api/sales-agent/dashboard`, { credentials: 'include' }),
        fetch(`${API}/api/b2b/prospects`, { credentials: 'include' }),
        fetch(`${API}/api/b2b/auto/bids?limit=20`, { credentials: 'include' }),
        fetch(`${API}/api/b2b/auto/discoveries?limit=20`, { credentials: 'include' })
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (prosRes.ok) { const d = await prosRes.json(); setProspects(Array.isArray(d) ? d : d.prospects || []); }
      if (bidsRes.ok) setBids(await bidsRes.json());
      if (discRes.ok) setDiscoveries(await discRes.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 3000); };

  const handleExport = (type) => { window.open(`${API}/api/export/${type}`, '_blank'); };

  return (
    <div data-testid="b2b-unified-page" className="space-y-6">
      <div className="flex justify-between items-center border-b border-slate-700 pb-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">B2B Centro de Operaciones</h1>
          <p className="text-slate-300 text-sm mt-1">Motor unificado: Inteligencia + Prospeccion + Ventas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('prospects')} data-testid="export-csv-btn">Exportar CSV</Button>
        </div>
      </div>

      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="prospects">Prospectos ({prospects.length})</TabsTrigger>
          <TabsTrigger value="bids">Licitaciones ({bids.length})</TabsTrigger>
          <TabsTrigger value="discoveries">Descubrimientos ({discoveries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          {dashboard && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Funnel */}
              <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
                <CardHeader><CardTitle className="text-white text-sm">Embudo de Conversion B2B</CardTitle></CardHeader>
                <CardContent>
                  <FunnelBar label="Descubrimientos" value={dashboard.funnel.discoveries} total={dashboard.funnel.discoveries} color="bg-slate-500" />
                  <FunnelBar label="Prospectos" value={dashboard.funnel.prospects} total={dashboard.funnel.discoveries} color="bg-blue-500" />
                  <FunnelBar label="Analizados" value={dashboard.funnel.analyzed} total={dashboard.funnel.prospects} color="bg-cyan-500" />
                  <FunnelBar label="Contactados" value={dashboard.funnel.contacted} total={dashboard.funnel.analyzed} color="bg-indigo-500" />
                  <FunnelBar label="Interesados" value={dashboard.funnel.interested} total={dashboard.funnel.contacted} color="bg-purple-500" />
                  <FunnelBar label="Citas" value={dashboard.funnel.meetings} total={dashboard.funnel.interested} color="bg-teal-500" />
                  <FunnelBar label="Cotizados" value={dashboard.funnel.quoted} total={dashboard.funnel.interested} color="bg-green-500" />
                  {dashboard.funnel.over_budget > 0 && <FunnelBar label="Sobre Presupuesto" value={dashboard.funnel.over_budget} total={dashboard.funnel.prospects} color="bg-red-500" />}
                </CardContent>
              </Card>

              {/* Revenue + Rates */}
              <div className="space-y-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-sm">Revenue Potencial</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between"><span className="text-slate-400 text-xs">Total Cotizado</span><span className="text-white font-bold">${dashboard.revenue.total_quoted?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400 text-xs">Firmado</span><span className="text-green-400 font-bold">${dashboard.revenue.signed_value?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400 text-xs">Pendiente</span><span className="text-yellow-400 font-bold">${dashboard.revenue.pending_value?.toLocaleString()}</span></div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader><CardTitle className="text-white text-sm">Tasas de Conversion</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between"><span className="text-slate-400 text-xs">Tasa Contacto</span><span className="text-white font-bold">{dashboard.rates.contact_rate}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-400 text-xs">Tasa Interes</span><span className="text-white font-bold">{dashboard.rates.interest_rate}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-400 text-xs">Tasa Cotizacion</span><span className="text-white font-bold">{dashboard.rates.quote_rate}%</span></div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="py-3 space-y-1">
                    <div className="flex justify-between text-xs"><span className="text-slate-400">Llamadas</span><span className="text-white">{dashboard.calls.completed}/{dashboard.calls.total}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-400">Bids</span><span className="text-white">{dashboard.bids}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-400">Con Consentimiento</span><span className="text-white">{dashboard.consent}</span></div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="prospects" className="mt-4">
          {selectedProspect ? (
            <ProspectDetail prospect={selectedProspect} onClose={() => setSelectedProspect(null)} />
          ) : (
            <div className="space-y-2">
              {prospects.sort((a, b) => (b.compatibility_score || 0) - (a.compatibility_score || 0)).map(p => (
                <div key={p.id} onClick={() => setSelectedProspect(p)}
                  className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 cursor-pointer hover:bg-slate-700/60">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-white font-medium">{p.company}</span>
                      <span className="text-slate-500 text-xs ml-2">{p.industry}</span>
                      {p.contact_name && <span className="text-blue-400 text-xs ml-2">{p.contact_name}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'interested' ? 'bg-green-900/40 text-green-300' : p.status === 'contacted' ? 'bg-blue-900/40 text-blue-300' : p.status === 'over_budget' ? 'bg-red-900/40 text-red-300' : 'bg-slate-700 text-slate-300'}`}>{p.status}</span>
                      <span className={`font-bold text-sm ${(p.compatibility_score || 0) >= 80 ? 'text-green-400' : (p.compatibility_score || 0) >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>{p.compatibility_score || 0}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bids" className="mt-4">
          <div className="space-y-2">
            {bids.map(b => (
              <div key={b.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                <div className="flex justify-between">
                  <div>
                    <p className="text-white font-medium text-sm">{b.title}</p>
                    <p className="text-slate-400 text-xs">{b.organization} | {b.location}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-green-400 font-bold text-sm">{b.relevance_score}/10</span>
                    {b.deadline && <p className="text-slate-500 text-xs">{b.deadline}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="discoveries" className="mt-4">
          <div className="space-y-2">
            {discoveries.map(d => (
              <div key={d.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                <div className="flex justify-between">
                  <div>
                    <p className="text-white font-medium text-sm">{d.company_name}</p>
                    <p className="text-slate-400 text-xs">{d.industry} | {d.location}</p>
                  </div>
                  <div className="text-right">
                    <span className={`font-bold text-sm ${d.compatibility_score >= 80 ? 'text-green-400' : 'text-yellow-400'}`}>{d.compatibility_score}%</span>
                    <p className={`text-xs ${d.status === 'discovered' ? 'text-yellow-400' : 'text-green-400'}`}>{d.status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
